/* a-garaia-cart-customizations.js
   Cart-side customization controller. Loaded once per cart page.
   Owns: per-row state initialization, single applyChange() mutation method,
   event wiring for gift wrap (Phase 6) and initial printing (Phase 7).
*/

(function () {
  "use strict";

  const CONFIG_SCRIPT_ID = "ga-customizations-config";
  const ROW_SELECTOR = "[data-ga-customize-row]";

  let config = null;
  const rows = new Map(); // lineKey -> Row instance

  function readConfig() {
    const node = document.getElementById(CONFIG_SCRIPT_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn("[ga-customize] config JSON parse failed", err);
      return null;
    }
  }

  function readRowState(rowEl) {
    const raw = rowEl.getAttribute("data-ga-addon-state");
    if (!raw) return {};
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn("[ga-customize] row state parse failed", err);
      return {};
    }
  }

  function readApplicable(rowEl) {
    const raw = rowEl.getAttribute("data-ga-applicable");
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch (err) {
      console.warn("[ga-customize] applicable parse failed", err);
      return [];
    }
  }

  let reconcileInFlight = false;
  async function reconcileOrphans() {
    if (reconcileInFlight) return;
    const node = document.getElementById("ga-orphan-fee-keys");
    if (!node) return;
    let keys;
    try {
      keys = JSON.parse(node.textContent).split(",").filter(Boolean);
    } catch (err) {
      console.warn("[ga-customize] orphan keys parse failed", err);
      return;
    }
    if (keys.length === 0) return;
    reconcileInFlight = true;
    try {
      const updates = {};
      keys.forEach((k) => { updates[k] = 0; });
      console.info("[ga-customize] removing", keys.length, "orphan fee line(s)");
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      // Re-render the cart section in place — no full reload. This will
      // produce a render without the orphan script tag, so subsequent
      // observer-triggered calls return early at the no-node check.
      await refreshCartSection();
    } catch (err) {
      console.warn("[ga-customize] orphan removal failed", err);
    } finally {
      reconcileInFlight = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Gift-wrap quantity sync (parent qty changes via Dawn's qty stepper)
  // ---------------------------------------------------------------------------

  async function syncGiftWrapQuantities(rows) {
    // Build a list of rows that have gift wrap on (no fetch yet).
    const candidates = [];
    for (const row of rows.values()) {
      const wantGW = !!(row.state && row.state.gift_wrap && row.state.gift_wrap.approved);
      if (wantGW) candidates.push(row);
    }
    if (candidates.length === 0) return;

    // One /cart.js fetch covers all candidates.
    let cart;
    try {
      cart = await (await fetch("/cart.js")).json();
    } catch (e) {
      console.warn("[ga-customize] gift-wrap qty sync: cart fetch failed", e);
      return;
    }

    const updates = {};
    for (const row of candidates) {
      const feeLine = cart.items.find((it) =>
        it.handle === "gift-wrap-fee" &&
        it.properties &&
        it.properties._linked_to_id === row.linkId
      );
      if (feeLine && feeLine.quantity !== row.quantity) {
        updates[feeLine.key] = row.quantity;
      }
    }

    if (Object.keys(updates).length === 0) return;

    console.info("[ga-customize] syncing gift-wrap fee qty to parent qty:", updates);
    try {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      // Re-render so the rendered subtotal reflects the corrected fee-line
      // quantity. The next pass through the post-render handler re-runs
      // this helper but finds nothing to sync (updates object is empty),
      // so there's no infinite loop.
      await refreshCartSection();
    } catch (e) {
      console.warn("[ga-customize] gift-wrap qty sync: /cart/update.js failed", e);
    }
  }

  // ---------------------------------------------------------------------------
  // Fee variant cache and lookup
  // ---------------------------------------------------------------------------

  const feeVariantCache = new Map(); // handle -> Map<priceValue, variantId>

  async function loadFeeVariants(handle) {
    if (feeVariantCache.has(handle)) return feeVariantCache.get(handle);
    let res;
    try {
      res = await fetch(`/products/${handle}.js`);
    } catch (err) {
      console.warn("[ga-customize] fee product fetch error", handle, err);
      return null;
    }
    if (!res.ok) {
      console.warn("[ga-customize] fee product fetch failed", handle, res.status);
      return null;
    }
    const product = await res.json();
    const map = new Map();
    product.variants.forEach((v) => {
      // v.price is the variant's price value as returned by /products/<handle>.js.
      // For TL (zero-decimal currency) this is the major-unit integer (e.g. 50, 100).
      // The data-ga-*-price-units attributes carry the same unit from Liquid metafield.
      // Both come from Shopify's money serialisation, so they match directly.
      map.set(v.price, v.id);
    });
    feeVariantCache.set(handle, map);
    console.info("[ga-customize] loaded", map.size, "variants for", handle);
    return map;
  }

  function nearestVariantId(map, priceValue) {
    if (!map || !priceValue) return null;
    // The variant grid spans 50-step values; the actual unit (major vs cents)
    // depends on what /products/<handle>.js returns for variant.price. We
    // detect by inspecting the smallest map key: if it's 50, units are major;
    // if it's 5000, units are cents.
    const keys = [...map.keys()].sort((a, b) => a - b);
    const minKey = keys[0];
    const stepInMapUnits = minKey; // STEP === MIN === smallest key
    const maxKey = keys[keys.length - 1];

    // Translate priceValue (data-attr unit) into the map's unit if mismatched.
    // If map keys look like cents (>=5000) but priceValue looks like major
    // (e.g. 100), scale up by 100. Symmetric scale-down if reverse.
    let scaledTarget = priceValue;
    if (minKey >= 5000 && priceValue < 1000) scaledTarget = priceValue * 100;
    else if (minKey < 1000 && priceValue >= 5000) scaledTarget = Math.round(priceValue / 100);

    let target = Math.round(scaledTarget / stepInMapUnits) * stepInMapUnits;
    if (target < minKey) target = minKey;
    if (target > maxKey) target = maxKey;

    console.info("[ga-customize] nearestVariantId", {
      priceValue, mapKeysSample: keys.slice(0, 3), scaledTarget, target,
      hit: map.has(target),
    });

    if (map.has(target)) return map.get(target);
    // Exact match failed — scan for nearest available key.
    let best = null;
    let bestDist = Infinity;
    map.forEach((id, price) => {
      const dist = Math.abs(price - target);
      if (dist < bestDist) { bestDist = dist; best = id; }
    });
    if (best === null) console.warn("[ga-customize] no variant found for priceValue", priceValue);
    return best;
  }

  // ---------------------------------------------------------------------------
  // Initial printing — validation helpers
  // ---------------------------------------------------------------------------

  const INITIALS_RE = /^[A-Za-zÂÇÎĞİÖŞÛÜâçîğıöşûü]{1,3}$/;
  const INITIALS_CHAR_RE = /[A-Za-zÂÇÎĞİÖŞÛÜâçîğıöşûü]/g;

  function isValidInitials(str) {
    return INITIALS_RE.test(str);
  }

  function cleanInitials(str) {
    const matched = (str || "").match(INITIALS_CHAR_RE) || [];
    return matched.slice(0, 3).join("").toUpperCase();
  }

  // ---------------------------------------------------------------------------
  // Cart section refresh
  // ---------------------------------------------------------------------------

  async function refreshCartSection() {
    // Read section IDs at call time (they're present in data-id on the DOM).
    const itemsEl = document.getElementById("main-cart-items");
    const summaryEl = document.getElementById("main-cart-footer");
    const sectionIds = [];
    if (itemsEl) sectionIds.push(itemsEl.dataset.id);
    if (summaryEl) sectionIds.push(summaryEl.dataset.id);
    if (sectionIds.length === 0) { window.location.reload(); return; }

    // Fetch from /cart, not / — main-cart-items / main-cart-footer only
    // exist on the cart template, so /?sections=... returns empty for them.
    let res;
    try {
      res = await fetch("/cart?sections=" + sectionIds.join(","));
    } catch (err) {
      window.location.reload();
      return;
    }
    if (!res.ok) { window.location.reload(); return; }
    const sections = await res.json();

    if (itemsEl && itemsEl.dataset.id && sections[itemsEl.dataset.id]) {
      const html = sections[itemsEl.dataset.id];
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const newRoot = tmp.querySelector("#main-cart-items");
      if (newRoot) itemsEl.replaceWith(newRoot);
    }
    if (summaryEl && summaryEl.dataset.id && sections[summaryEl.dataset.id]) {
      const html = sections[summaryEl.dataset.id];
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const newRoot = tmp.querySelector("#main-cart-footer");
      if (newRoot) {
        const currentSummary = document.getElementById("main-cart-footer");
        if (currentSummary) currentSummary.replaceWith(newRoot);
      }
    }

    // Re-init the controller for the new DOM.
    rows.clear();
    document.querySelectorAll(ROW_SELECTOR).forEach((el) => {
      const row = new Row(el);
      if (row.lineKey) {
        rows.set(row.lineKey, row);
        bindToggle(el);
        bindGiftWrap(el, row);
        bindInitialPrint(el, row);
      }
    });

    // Reconcile any per-unit slots that exceed the (possibly reduced) parent
    // quantity (Task 7.3: trim orphaned slots after Dawn's qty stepper fires).
    for (const row of rows.values()) {
      const slots = row.state && row.state.initial_print && row.state.initial_print.slots;
      if (!slots) continue;
      if (slots.length > row.quantity) {
        const next = JSON.parse(JSON.stringify(row.state));
        next.initial_print.slots = slots.slice(0, row.quantity);
        // Async — the cart will re-render again automatically.
        row.applyChange(next);
      }
    }

    // Sync gift-wrap fee line quantity to parent line quantity for any row
    // that has gift wrap on (per-unit pricing — fee scales with parent qty
    // even when the parent qty changes via Dawn's qty stepper). One fetch +
    // one batched /cart/update.js for all rows that need a sync. Async —
    // the helper handles its own errors and triggers refreshCartSection
    // internally if any updates were applied.
    syncGiftWrapQuantities(rows);

    // Re-attach the mutation observer to the new #main-cart-items element
    // (the prior observer was watching the old, now-detached element).
    startCartMutationObserver();
    // The fresh render may include a new orphan-keys script tag (if the
    // cart was mutated externally — e.g., user clicked a Remove button
    // outside of our applyChange path). Reconcile.
    reconcileOrphans();
  }

  // ---------------------------------------------------------------------------
  // Watch for external cart mutations (Dawn's cart.js partial re-renders)
  // ---------------------------------------------------------------------------

  let cartObserver = null;
  function startCartMutationObserver() {
    if (cartObserver) {
      cartObserver.disconnect();
      cartObserver = null;
    }
    const target = document.getElementById("main-cart-items");
    if (!target) return;
    cartObserver = new MutationObserver(() => {
      // Dawn replaced or modified the cart section. Re-init rows and check
      // for orphans the new render flagged.
      rows.clear();
      document.querySelectorAll(ROW_SELECTOR).forEach((el) => {
        const row = new Row(el);
        if (row.lineKey) {
          rows.set(row.lineKey, row);
          bindToggle(el);
          bindGiftWrap(el, row);
          bindInitialPrint(el, row);
        }
      });
      reconcileOrphans();
    });
    cartObserver.observe(target, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Row class — per-line state + single mutation method
  // ---------------------------------------------------------------------------

  function generateLinkId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "lid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  class Row {
    constructor(rowEl) {
      this.el = rowEl;
      this.lineKey = rowEl.getAttribute("data-ga-line-key");
      this.applicable = readApplicable(rowEl);
      this.state = readRowState(rowEl);
      this.busy = false;
      // Stable link id for matching fees to this parent. Survives parent
      // property changes (which mutate this.lineKey).
      this.linkId = rowEl.getAttribute("data-ga-addon-link-id") || "";
      // Price values are in the shop's smallest currency unit (TL units for TRY).
      this.initialPriceUnits = parseInt(rowEl.getAttribute("data-ga-initial-price-units") || "0", 10);
      this.giftwrapPriceUnits = parseInt(rowEl.getAttribute("data-ga-giftwrap-price-units") || "0", 10);
      this.quantity = parseInt(rowEl.getAttribute("data-ga-line-quantity") || "1", 10);
    }

    setBusy(on) {
      this.busy = on;
      this.el.classList.toggle("ga-customize__row-busy", on);
    }

    async applyChange(nextState) {
      if (this.busy) return;
      this.setBusy(true);
      try {
        const { parentChange, removals, addItems } = await this.buildPayload(nextState);

        // Phase 1: remove any fee lines we no longer want (batch via update.js).
        if (removals.length > 0) {
          const updates = {};
          removals.forEach((k) => { updates[k] = 0; });
          console.info("[ga-customize] removing fee lines", removals);
          const res = await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates }),
          });
          if (!res.ok) throw new Error("cart update (removals) failed: " + res.status);
        }

        // Phase 2: update the parent line's properties (and quantity).
        // /cart/change.js is the only endpoint that updates line-item properties;
        // /cart/update.js silently ignores properties for existing lines.
        if (parentChange) {
          console.info("[ga-customize] updating parent line", this.lineKey, parentChange);
          const res = await fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: this.lineKey,
              quantity: parentChange.quantity,
              properties: parentChange.properties,
            }),
          });
          if (!res.ok) {
            const body = await res.text();
            console.warn("[ga-customize] change.js failed", res.status, body);
            throw new Error("change parent failed: " + res.status);
          }
        }

        // Phase 3: add any new fee items.
        for (const item of (addItems || [])) {
          console.info("[ga-customize] adding fee item", item);
          const res = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });
          if (!res.ok) {
            const body = await res.text();
            console.warn("[ga-customize] add.js failed", res.status, body);
            throw new Error("cart add failed: " + res.status + " " + body);
          }
        }

        await refreshCartSection();
      } catch (err) {
        console.warn("[ga-customize] applyChange failed", err);
        try {
          await refreshCartSection();
        } catch (_) {
          alert("Couldn't save customization. Reloading.");
          window.location.reload();
        }
      } finally {
        this.setBusy(false);
      }
    }

    async buildPayload(nextState) {
      // Ensure parent has a stable _addon_link_id; mint one on first toggle.
      if (!this.linkId) this.linkId = generateLinkId();

      const parentProps = this.computeDisplayProperties(nextState);
      parentProps._addon_state = JSON.stringify(nextState);
      parentProps._addon_link_id = this.linkId;
      const parentChange = {
        quantity: this.quantity,
        properties: parentProps,
      };

      const removals = []; // line keys to remove (set quantity 0 via /cart/update.js)
      const addItems = []; // items for /cart/add.js

      // Gift wrap (per_line). When the toggle state changes, sweep ALL existing
      // linked gift-wrap-fee lines for this row (matched via link id, not
      // line key — line key changes whenever properties change) and clear
      // them, then add a fresh fee if turning on.
      const wantGiftWrap = !!(nextState.gift_wrap && nextState.gift_wrap.approved);
      const haveGiftWrap = !!(this.state.gift_wrap && this.state.gift_wrap.approved);
      if (wantGiftWrap !== haveGiftWrap) {
        const cart = await (await fetch("/cart.js")).json();
        cart.items.forEach((it) => {
          if (it.handle === "gift-wrap-fee" &&
              it.properties &&
              it.properties._linked_to_id === this.linkId) {
            removals.push(it.key);
          }
        });
        if (wantGiftWrap) {
          const map = await loadFeeVariants("gift-wrap-fee");
          const variantId = nearestVariantId(map, this.giftwrapPriceUnits);
          if (variantId) {
            addItems.push({
              id: variantId,
              quantity: this.quantity, // per-unit pricing — fee scales with parent line qty
              properties: { _linked_to_id: this.linkId, _hide_in_cart: "true" },
            });
          } else {
            console.warn("[ga-customize] gift-wrap fee variant not found");
          }
        }
      }

      // Initial print (per_unit): adjust fee line quantity to match approved slot count.
      const wantApproved = (nextState.initial_print && nextState.initial_print.slots)
        ? nextState.initial_print.slots.filter((s) => s && s.approved).length
        : 0;
      const haveApproved = (this.state.initial_print && this.state.initial_print.slots)
        ? this.state.initial_print.slots.filter((s) => s && s.approved).length
        : 0;
      if (wantApproved !== haveApproved) {
        const ipCart = await (await fetch("/cart.js")).json();
        const feeLine = ipCart.items.find((it) =>
          it.handle === "initial-printing-fee" &&
          it.properties &&
          it.properties._linked_to_id === this.linkId
        );
        if (wantApproved === 0 && feeLine) {
          removals.push(feeLine.key);
        } else if (wantApproved > 0 && !feeLine) {
          // Add the fee line for the first time.
          const map = await loadFeeVariants("initial-printing-fee");
          const variantId = nearestVariantId(map, this.initialPriceUnits);
          if (variantId) {
            addItems.push({
              id: variantId,
              quantity: wantApproved,
              properties: { _linked_to_id: this.linkId, _hide_in_cart: "true" },
            });
          } else {
            console.warn("[ga-customize] initial-printing fee variant not found");
          }
        } else if (wantApproved > 0 && feeLine) {
          // Fee line exists; update its quantity via /cart/update.js (quantity only,
          // no property change needed here — /cart/update.js handles qty fine).
          const qtyUpdates = {};
          qtyUpdates[feeLine.key] = wantApproved;
          console.info("[ga-customize] updating initial-printing-fee qty to", wantApproved);
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ updates: qtyUpdates }),
          });
        }
      }

      return { parentChange, removals, addItems };
    }

    computeDisplayProperties(nextState) {
      const props = {};

      // Gift wrap display strings.
      if (nextState.gift_wrap && nextState.gift_wrap.approved) {
        props["Gift wrap"] = "Yes";
        const msg = nextState.gift_wrap.message || "";
        if (msg) props["Gift message"] = msg;
      }

      // Initial printing display — pipe-separated initials string, "_" for empty slots.
      if (nextState.initial_print && nextState.initial_print.slots && nextState.initial_print.slots.length > 0) {
        const parts = nextState.initial_print.slots.map((s) => (s && s.approved && s.v) ? s.v : "_");
        if (parts.some((p) => p !== "_")) {
          props["Initials"] = parts.join(" | ");
        }
      }

      return props;
    }
  }

  // ---------------------------------------------------------------------------
  // Event binders
  // ---------------------------------------------------------------------------

  function bindToggle(rowEl) {
    const dropdown = rowEl.querySelector(".ga-customize");
    if (!dropdown) return;
    isolateInputsFromCart(dropdown);
    const toggle = dropdown.querySelector("[data-ga-customize-toggle]");
    if (!toggle || toggle.dataset.gaToggleBound === "1") return;
    toggle.dataset.gaToggleBound = "1";
    toggle.addEventListener("click", () => {
      const isOpen = dropdown.getAttribute("data-open") === "true";
      dropdown.setAttribute("data-open", String(!isOpen));
      toggle.setAttribute("aria-expanded", String(!isOpen));
    });
  }

  // Stop change/input events from inputs inside the customize panel from
  // bubbling up to Dawn's <cart-items> change listener — Dawn treats every
  // change event as a quantity-input change and runs validateQuantity on it,
  // which produces a bogus "increments of" popup for our checkboxes/textareas
  // (because parseInt("on") === NaN, NaN % NaN !== 0 → step_error).
  function isolateInputsFromCart(dropdown) {
    if (dropdown.dataset.gaIsolated === "1") return;
    dropdown.dataset.gaIsolated = "1";
    const stop = (e) => e.stopPropagation();
    dropdown.addEventListener("change", stop);
    dropdown.addEventListener("input", stop);
  }

  function bindGiftWrap(rowEl, row) {
    if (rowEl.dataset.gaGiftBound === "1") return;
    const checkbox = rowEl.querySelector("[data-ga-giftwrap-toggle]");
    const messageWrap = rowEl.querySelector("[data-ga-giftwrap-message-wrap]");
    const messageInput = rowEl.querySelector("[data-ga-giftwrap-message]");
    const counter = rowEl.querySelector("[data-ga-giftwrap-counter]");
    const status = rowEl.querySelector("[data-ga-giftwrap-status]");
    if (!checkbox) return;
    rowEl.dataset.gaGiftBound = "1";

    function reflectState() {
      const approved = checkbox.checked;
      if (messageWrap) messageWrap.hidden = !approved;
      if (status) status.hidden = !approved;
    }

    reflectState();

    checkbox.addEventListener("change", () => {
      const next = JSON.parse(JSON.stringify(row.state || {}));
      next.gift_wrap = next.gift_wrap || {};
      next.gift_wrap.approved = checkbox.checked;
      if (!checkbox.checked) {
        next.gift_wrap.message = "";
        if (messageInput) messageInput.value = "";
      } else {
        next.gift_wrap.message = messageInput ? messageInput.value : "";
      }
      reflectState();
      row.applyChange(next);
    });

    if (messageInput) {
      messageInput.addEventListener("input", () => {
        if (counter) counter.textContent = `${messageInput.value.length} / 150`;
      });
      messageInput.addEventListener("blur", () => {
        if (!checkbox.checked) return;
        const next = JSON.parse(JSON.stringify(row.state || {}));
        next.gift_wrap = next.gift_wrap || { approved: true };
        next.gift_wrap.message = messageInput.value;
        row.applyChange(next);
      });
    }
  }

  function bindInitialPrint(rowEl, row) {
    if (rowEl.dataset.gaInitBound === "1") return;
    const master = rowEl.querySelector("[data-ga-initialprint-master]");
    const grid = rowEl.querySelector("[data-ga-initialprint-grid]");
    if (!master || !grid) return;
    rowEl.dataset.gaInitBound = "1";

    function reflectMaster() {
      if (grid) grid.hidden = !master.checked;
    }
    reflectMaster();

    master.addEventListener("change", () => {
      reflectMaster();
      if (!master.checked) {
        // Clear all slots and remove the fee line.
        const next = JSON.parse(JSON.stringify(row.state || {}));
        next.initial_print = { slots: [] };
        row.applyChange(next);
      }
    });

    grid.querySelectorAll(".ga-customize__slot-row").forEach((slotRow) => {
      const idx = parseInt(slotRow.getAttribute("data-ga-slot-index"), 10);
      const input = slotRow.querySelector("[data-ga-slot-input]");
      const approve = slotRow.querySelector("[data-ga-slot-approve]");
      const remove = slotRow.querySelector("[data-ga-slot-remove]");
      const error = slotRow.querySelector("[data-ga-slot-error]");

      if (input && approve) {
        input.addEventListener("input", () => {
          input.value = input.value.toUpperCase();
          const valid = isValidInitials(input.value);
          approve.disabled = !valid;
          if (error) {
            error.hidden = true;
            input.setAttribute("aria-invalid", "false");
          }
        });
        input.addEventListener("paste", (e) => {
          e.preventDefault();
          const pasted = (e.clipboardData || window.clipboardData).getData("text");
          input.value = cleanInitials(pasted);
          input.dispatchEvent(new Event("input"));
        });
        approve.addEventListener("click", () => {
          if (!isValidInitials(input.value)) {
            if (error) {
              error.textContent = "Type 1–3 letters.";
              error.hidden = false;
              input.setAttribute("aria-invalid", "true");
            }
            return;
          }
          const next = JSON.parse(JSON.stringify(row.state || {}));
          next.initial_print = next.initial_print || { slots: [] };
          while (next.initial_print.slots.length <= idx) {
            next.initial_print.slots.push({ v: "", approved: false });
          }
          next.initial_print.slots[idx] = { v: input.value, approved: true };
          row.applyChange(next);
        });
      }

      if (remove) {
        remove.addEventListener("click", () => {
          const next = JSON.parse(JSON.stringify(row.state || {}));
          if (next.initial_print && next.initial_print.slots && next.initial_print.slots[idx]) {
            next.initial_print.slots[idx] = { v: "", approved: false };
          }
          row.applyChange(next);
        });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  function init() {
    reconcileOrphans();
    config = readConfig();
    if (!config) return;
    document.querySelectorAll(ROW_SELECTOR).forEach((el) => {
      const row = new Row(el);
      if (row.lineKey) {
        rows.set(row.lineKey, row);
        bindToggle(el);
        bindGiftWrap(el, row);
        bindInitialPrint(el, row);
      }
    });
    startCartMutationObserver();
    window.gaCartCustomizations = { config, rows };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
